import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { fmtMoney, fmtRPM } from "@/lib/defaults";
import { useT } from "@/lib/i18n";
import { CALC } from "@/constants/testIds";
import { exportComparisonPDF } from "@/lib/pdf";
import { FileText } from "@phosphor-icons/react";

const Column = ({ label, load }) => (
  <div className="flex-1 min-w-0 rounded-xl border border-slate-200 bg-white p-5">
    <div className="flex items-center justify-between mb-3">
      <span className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-medium">
        {label}
      </span>
      {load.is_profitable ? (
        <Badge className="bg-emerald-600 text-white hover:bg-emerald-600 rounded-md">
          PROFITABLE
        </Badge>
      ) : (
        <Badge className="bg-red-600 text-white hover:bg-red-600 rounded-md">
          NOT PROFITABLE
        </Badge>
      )}
    </div>
    <p className="text-sm font-semibold text-slate-900 leading-tight">
      {load.pick_city} → {load.delivery_city}
    </p>
    <p className="text-xs text-slate-500 mt-1">{load.commodity}</p>

    <div className="mt-4 space-y-2 text-sm">
      {[
        ["Total Miles", Math.round(load.total_miles).toLocaleString()],
        ["Real RPM", fmtRPM(load.real_rpm)],
        ["Rate", fmtMoney(load.rate)],
        ["Trip Cost", fmtMoney(load.trip_cost)],
        ["Driver Pay", fmtMoney(load.driver_pay)],
        ["Dispatcher Fee", fmtMoney(load.dispatcher_fee)],
      ].map(([k, v]) => (
        <div key={k} className="flex items-center justify-between border-b border-slate-100 pb-2">
          <span className="text-slate-500 text-xs uppercase tracking-wide">{k}</span>
          <span className="tabular font-medium text-slate-900">{v}</span>
        </div>
      ))}
      <div className="flex items-center justify-between pt-2">
        <span className="text-slate-700 text-xs uppercase tracking-wide font-semibold">
          Net Profit
        </span>
        <span
          className={`tabular text-lg font-semibold ${load.net_profit >= 0 ? "text-emerald-700" : "text-red-600"}`}
        >
          {fmtMoney(load.net_profit)}
        </span>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-slate-500 text-xs uppercase tracking-wide">
          Profit / Mile
        </span>
        <span className="tabular font-medium">
          {fmtRPM(load.profit_per_mile)}
        </span>
      </div>
    </div>
  </div>
);

export const CompareDialog = ({ lang, open, onOpenChange, loads }) => {
  const t = useT(lang);
  const [a, b] = loads || [];
  if (!a || !b) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={CALC.compareDialog}
        className="max-w-4xl bg-white"
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold tracking-tight">
            {t("compare")}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col md:flex-row gap-4 mt-2">
          <Column label="Load A" load={a} />
          <Column label="Load B" load={b} />
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button
            data-testid={CALC.btnExportCompare}
            variant="default"
            className="bg-blue-700 hover:bg-blue-800 rounded-lg"
            onClick={() => exportComparisonPDF(a, b)}
          >
            <FileText size={16} weight="bold" className="me-2" />
            {t("exportPdf")}
          </Button>
          <Button
            data-testid={CALC.btnCloseCompare}
            variant="outline"
            className="rounded-lg border-slate-300"
            onClick={() => onOpenChange(false)}
          >
            {t("closeCompare")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
